import { isEqual, orderBy } from 'lodash';
import { Component, Fragment, KeyboardEvent, MouseEvent } from 'react';
import { KeyValue } from '@mews/enums';
import {
    Checkbox,
    Container,
    EmptyState,
    EmptyStateProps,
    IconButton,
    IconName,
    Identified,
    LoadingOverlay,
    Spinner,
    Stack,
} from '@optimus-web/core';
import MatrixHeaderCellElement from './MatrixHeaderCell';
import {
    ColumnDefinitionsElement,
    ColumnElement,
    MatrixBodyElement,
    MatrixCellElement,
    MatrixElement,
    MatrixFooterElement,
    MatrixHeadElement,
    MatrixHeaderRowElement,
    MatrixRowElement,
    MatrixWrapper,
} from './style';
import { Bordered, MatrixCellColor, MatrixMainMapping, MatrixTextVariant, SortOrder } from './types';

interface MatrixCellProps {
    children?: React.ReactNode;
    onEdit?: (value: string) => void;
}

export interface MatrixMapping<T> extends MatrixMainMapping<T> {
    getTextVariant?: (source: T) => MatrixTextVariant | undefined;
    getCellColor?: (source: T) => MatrixCellColor | undefined;
    onCellClick?: ClickHandler<T>;
    render?: (source: T, props: MatrixCellProps) => React.ReactNode;
    columnWidth?: string;
    columnContentAlignment?: 'start' | 'end' | 'center';
    columnVerticalAlignment?: 'top' | 'bottom' | 'middle';
    sortable?: boolean;
    highlighted?: boolean | ((data: T, cellType: 'head' | 'body') => boolean);
    emphasized?: boolean | ((data: T, cellType: 'head' | 'body') => boolean);
    selectedCell?: boolean | ((data: T) => boolean);
}

type MatrixClickableElement = HTMLTableRowElement | HTMLTableCellElement;

type ClickHandler<T> = (
    event: KeyboardEvent<MatrixClickableElement> | MouseEvent<MatrixClickableElement>,
    value: T,
    columnIndex?: number
) => void;

export interface MatrixProps<T> extends Bordered {
    fixedHeader?: boolean;
    fixedFirstColumn?: boolean;
    highlightedHeader?: boolean;
    headerless?: boolean;
    mapping: Array<MatrixMapping<T>>;
    source: Array<T>;
    footerSource?: Array<T>;
    onEdit?: (value: string) => void;
    onColumnClick?: ClickHandler<T>;
    onRowClick?: ClickHandler<T>;
    onFooterRowClick?: ClickHandler<T>;
    maxHeight?: string;
    maxWidth?: string;
    rowSelection?: RowSelection<T>;
    initiallyCollapsed?: boolean;
    collapseAll?: boolean;
    dataTestName?: string;
    emptyState?: EmptyStateProps;
    loading?: boolean;
}

export interface RowSelection<T> {
    selectedData: Record<string, Identified>;
    onSelect: (checked: boolean, idenfifiedObject: Identified & T) => void;
}
interface MatrixState {
    sortColumn: number | null;
    sortOrder: SortOrder;
    currentHoveredColumnIndex: number | null;
    collapsedSections: Array<string>;
}

interface MatrixRowOptions<T> {
    clickHandler?: MatrixProps<T>['onRowClick'] | MatrixProps<T>['onFooterRowClick'];
}

export const matrixFactory = ({
    Matrix = MatrixElement,
    MatrixHeader = MatrixHeadElement,
    MatrixBody = MatrixBodyElement,
    MatrixHeaderRow = MatrixHeaderRowElement,
    MatrixRow = MatrixRowElement,
    MatrixCell = MatrixCellElement,
    ColumnDefinitions = ColumnDefinitionsElement,
    Column = ColumnElement,
    MatrixHeaderCell = MatrixHeaderCellElement,
    MatrixFooter = MatrixFooterElement,
} = {}) =>
    class MatrixComponent<T extends Identified & { subRows?: Array<T> }> extends Component<
        MatrixProps<T>,
        MatrixState
    > {
        static defaultProps = {
            mapping: [],
            source: [],
        };

        state: MatrixState = {
            sortColumn: null,
            sortOrder: SortOrder.Descending,
            currentHoveredColumnIndex: null,
            collapsedSections: this.props.initiallyCollapsed
                ? this.props.source.map((item) => (item.subRows ? item.id : ''))
                : [],
        };

        componentDidUpdate(prevProps: { collapseAll?: boolean; source: T[] }) {
            const { collapseAll, source } = this.props;
            const isSourcesDeepEqual = isEqual(source, prevProps.source);

            if (collapseAll !== prevProps?.collapseAll || !isSourcesDeepEqual) {
                this.setState({
                    collapsedSections: collapseAll
                        ? []
                        : source.map((item) => (item.subRows ? item.id : '')).filter(Boolean),
                });
            }
        }

        handleSort = (columnIndex: number) => {
            const column = this.props.mapping[columnIndex];
            const { sortColumn, sortOrder } = this.state;

            if (sortColumn === columnIndex) {
                this.setState({
                    sortOrder: sortOrder === SortOrder.Ascending ? SortOrder.Descending : SortOrder.Ascending,
                });
            } else if (column.valueKey) {
                this.setState({ sortColumn: columnIndex, sortOrder: SortOrder.Descending });
            }
        };

        createClickHandler =
            (data: T, clickHandler: ClickHandler<T>, columnIndex?: number) =>
            (event: MouseEvent<MatrixClickableElement>) =>
                clickHandler(event, data, columnIndex);

        createKeyDownHandler =
            (data: T, clickHandler: ClickHandler<T>, columnIndex?: number) =>
            (event: KeyboardEvent<HTMLTableRowElement>) => {
                const key = event.key === ' ' ? KeyValue.Spacebar : event.key;

                if (key === KeyValue.Enter || key === KeyValue.Spacebar) {
                    clickHandler(event, data, columnIndex);
                }
            };

        handleToggleSection = (id: string) => {
            const { collapsedSections } = this.state;
            const nextCollapsedSections = collapsedSections.includes(id)
                ? collapsedSections.filter((item) => item !== id)
                : [...collapsedSections, id];

            this.setState({
                collapsedSections: nextCollapsedSections,
            });
        };

        createClickableProps = (data: T, clickHandler: ClickHandler<T>, columnIndex?: number) => ({
            role: 'button',
            tabIndex: 0,
            onClick: this.createClickHandler(data, clickHandler, columnIndex),
            onKeyDown: this.createKeyDownHandler(data, clickHandler, columnIndex),
        });

        mapColumnWiseHeader = () => {
            const { mapping, source, bordered, fixedFirstColumn, rowSelection } = this.props;
            const { sortColumn, sortOrder } = this.state;

            return (
                <>
                    {rowSelection ? <MatrixHeaderCell title=" " valueKey="selection" bordered={bordered} /> : null}
                    {mapping.map(
                        (
                            {
                                title,
                                valueKey,
                                headerAlignment,
                                columnAlignment,
                                id,
                                sortable,
                                emphasized,
                                highlighted,
                            },
                            index
                        ) => (
                            <MatrixHeaderCell
                                columnAlignment={headerAlignment || columnAlignment}
                                valueKey={valueKey}
                                title={title}
                                key={id || index}
                                sortedBy={index === sortColumn}
                                columnIndex={index}
                                bordered={bordered}
                                sortable={sortable}
                                fixedFirstColumn={fixedFirstColumn}
                                emphasized={
                                    typeof emphasized === 'function' ? emphasized(source[0], 'head') : emphasized
                                }
                                highlighted={
                                    typeof highlighted === 'function' ? highlighted(source[0], 'head') : highlighted
                                }
                                onSort={this.handleSort}
                                sortOrder={sortOrder}
                            />
                        )
                    )}
                </>
            );
        };

        mapColumnWise = (
            source: Array<T>,
            depth = 0,
            { clickHandler }: MatrixRowOptions<T> = {}
        ): React.ReactNodeArray => {
            const { bordered, rowSelection } = this.props;
            const { collapsedSections } = this.state;

            return source.map((sourceItem, index: number) => (
                <Fragment key={sourceItem.id || index}>
                    <MatrixRow
                        bordered={bordered}
                        data-test-table-row={sourceItem.id || index}
                        selected={rowSelection && rowSelection.selectedData[sourceItem.id] !== undefined}
                        {...(clickHandler ? this.createClickableProps(sourceItem, clickHandler) : {})}
                    >
                        {rowSelection ? (
                            <MatrixCell bordered={bordered} depth={depth}>
                                <Checkbox
                                    value={`row-checkbox-${sourceItem.id}`}
                                    checked={rowSelection.selectedData[sourceItem.id] !== undefined}
                                    onChange={(event, checked) => rowSelection.onSelect(checked, sourceItem)}
                                />
                            </MatrixCell>
                        ) : null}
                        {this.renderMatrixCells(sourceItem, depth)}
                    </MatrixRow>
                    {sourceItem.subRows && !collapsedSections.includes(sourceItem.id)
                        ? this.mapColumnWise(sourceItem.subRows, depth + 1)
                        : null}
                </Fragment>
            ));
        };

        renderMatrixCells = (sourceItem: T, depth: number) => {
            const { currentHoveredColumnIndex } = this.state;
            const { mapping, source, fixedFirstColumn, footerSource, bordered, onColumnClick, onRowClick, ...other } =
                this.props;

            return mapping
                ? mapping.map((mappingItem: MatrixMapping<T>, columnIndex: number) => {
                      const isCurrentColumnHovered = currentHoveredColumnIndex === columnIndex;

                      const columnInteractionProps =
                          columnIndex > 0 && onColumnClick
                              ? {
                                    onMouseEnter: () => this.setState({ currentHoveredColumnIndex: columnIndex }),
                                    onMouseLeave: () => this.setState({ currentHoveredColumnIndex: null }),
                                    onFocus: () => this.setState({ currentHoveredColumnIndex: columnIndex }),
                                    onBlur: () => this.setState({ currentHoveredColumnIndex: null }),
                                    ...this.createClickableProps(sourceItem, onColumnClick, columnIndex),
                                }
                              : {};
                      const isExpandableCell = !!sourceItem.subRows && columnIndex === 0;
                      const expandableIcon = this.state.collapsedSections.includes(sourceItem.id)
                          ? IconName.ChevronDown
                          : IconName.ChevronUp;

                      const cellInteractionProps =
                          columnIndex > 0 && mappingItem.onCellClick
                              ? this.createClickableProps(sourceItem, mappingItem.onCellClick, columnIndex)
                              : {};

                      const renderContent = mappingItem.render
                          ? mappingItem.render(sourceItem, other)
                          : (sourceItem[mappingItem.valueKey] as React.ReactNode);

                      return (
                          <MatrixCell
                              key={mappingItem.id || columnIndex}
                              depth={depth}
                              bordered={bordered}
                              cellColor={mappingItem.getCellColor && mappingItem.getCellColor(sourceItem)}
                              columnAlignment={mappingItem.columnAlignment}
                              columnHovered={isCurrentColumnHovered}
                              fixedFirstColumn={fixedFirstColumn}
                              verticalAlign={mappingItem.columnVerticalAlignment}
                              emphasized={
                                  typeof mappingItem.emphasized === 'function'
                                      ? mappingItem.emphasized(sourceItem, 'body')
                                      : mappingItem.emphasized
                              }
                              highlighted={
                                  typeof mappingItem.highlighted === 'function'
                                      ? mappingItem.highlighted(sourceItem, 'body')
                                      : mappingItem.highlighted
                              }
                              hovered={!!mappingItem.onCellClick}
                              selectedCell={
                                  typeof mappingItem.selectedCell === 'function'
                                      ? mappingItem.selectedCell(sourceItem)
                                      : mappingItem.selectedCell
                              }
                              textVariant={mappingItem.getTextVariant && mappingItem.getTextVariant(sourceItem)}
                              data-test-table-cell={mappingItem.valueKey ?? columnIndex}
                              $width={mappingItem.columnWidth}
                              {...columnInteractionProps}
                              {...cellInteractionProps}
                          >
                              {isExpandableCell ? (
                                  <Stack
                                      alignment={mappingItem.columnContentAlignment}
                                      itemAlignment="center"
                                      spacing="100"
                                  >
                                      <IconButton
                                          icon={expandableIcon}
                                          onClick={() => this.handleToggleSection(sourceItem.id)}
                                          size="small"
                                          variant="ghost"
                                          type="button"
                                      />
                                      <div>{renderContent}</div>
                                  </Stack>
                              ) : (
                                  renderContent
                              )}
                          </MatrixCell>
                      );
                  })
                : null;
        };

        render() {
            const {
                mapping,
                source,
                footerSource,
                onEdit,
                bordered,
                onRowClick,
                onFooterRowClick,
                fixedHeader,
                fixedFirstColumn,
                highlightedHeader,
                headerless,
                maxHeight,
                maxWidth,
                rowSelection,
                dataTestName,
                emptyState,
                loading,
                ...other
            } = this.props;

            const { sortColumn, sortOrder } = this.state;

            let sortedSource = source;

            if (sortColumn) {
                const { valueKey } = mapping[sortColumn];
                sortedSource = orderBy(sortedSource, valueKey, sortOrder);
            }

            const renderEmptyState = () => {
                if (sortedSource.length > 0 || !emptyState) {
                    return null;
                }

                if (loading) {
                    return <Container padding={['900', '0']} />;
                }

                return (
                    <Container padding="200" data-test-id="matrix-empty-state">
                        <EmptyState {...emptyState} />
                    </Container>
                );
            };

            const columnDefinitions = mapping
                ? mapping.map((m, index) => <Column key={m.id || index} columnWidth={m.columnWidth} />)
                : [];

            return (
                <MatrixWrapper
                    bordered={bordered}
                    maxHeight={maxHeight}
                    maxWidth={maxWidth}
                    data-test-table={dataTestName}
                    aria-disabled={loading}
                >
                    <Matrix {...other} bordered={bordered} data-test-id="matrix-state">
                        <ColumnDefinitions>
                            {columnDefinitions.length !== 0 && rowSelection
                                ? [<Column key="selection" columnWidth="0" />, ...columnDefinitions]
                                : columnDefinitions}
                        </ColumnDefinitions>
                        {headerless ? null : (
                            <MatrixHeader fixedHeader={fixedHeader} highlighted={highlightedHeader}>
                                <MatrixHeaderRow bordered={bordered}>{this.mapColumnWiseHeader()}</MatrixHeaderRow>
                            </MatrixHeader>
                        )}
                        <MatrixBody isLoading={loading}>
                            {this.mapColumnWise(sortedSource, 0, {
                                clickHandler: onRowClick,
                            })}
                        </MatrixBody>
                        {footerSource ? (
                            <MatrixFooter>
                                {this.mapColumnWise(footerSource, 0, {
                                    clickHandler: onFooterRowClick,
                                })}
                            </MatrixFooter>
                        ) : null}
                    </Matrix>
                    {renderEmptyState()}
                    {loading ? (
                        <LoadingOverlay show={loading} data-test-id="matrix-loading-state">
                            <Spinner />
                        </LoadingOverlay>
                    ) : null}
                </MatrixWrapper>
            );
        }
    };

export const Matrix = matrixFactory();
